import { StatefulOrderResponse } from "models/order-management/statefullOrderResponse";

export interface AllowedProps {
  [key: string]: AllowedPropType;
}

export interface AllowedPropType {
  [key: string]: AllowedProp;
}

export type DefaultValueFn = (
  order: StatefulOrderResponse,
  property: keyof StatefulOrderResponse
) => string;

export interface AllowedProp {
  label: string;
  size: number;
  position: number;
  min?: boolean;
  capitalizeValue?: boolean;
  defaultValue?: string | DefaultValueFn;
}

export const ALLOWED_PROPS_MEDICATION_COMPOUND: AllowedProps = {
  MEDICATION: {
    /**************************************************************************
     * FIRST ROW                                                              *
     **************************************************************************/
    dosageStrength: {
      label: "Strength",
      size: 20,
      position: 19,
      capitalizeValue: false,
      defaultValue: (order, _property) => {
        if (order.orderType === "MEDICATION" && order.orderSubtype === "ERX") {
          return order.isCompound
            ? "compound"
            : "No Details Provided (via ERX)";
        }
        return "No Details Entered (via User)";
      },
    },
    dosageDosage: {
      label: "Dosage",
      size: 20,
      position: 18,
      capitalizeValue: false,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    dosageForm: {
      label: "Dosage Form",
      size: 20,
      position: 17,
      capitalizeValue: false,
      defaultValue: (order, _property) => {
        if (order.orderType === "MEDICATION" && order.orderSubtype === "ERX") {
          return order.isCompound
            ? "compound"
            : "No Details Provided (via ERX)";
        }
        return "No Details Entered (via User)";
      },
    },
    dosageRoute: {
      label: "Route",
      size: 20,
      position: 16,
      capitalizeValue: false,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    createdDatetime: {
      label: "Order Enter Date",
      size: 20,
      position: 15,
      capitalizeValue: false,
      defaultValue: "-",
    },
    /**************************************************************************
     * SECOND ROW                                                             *
     **************************************************************************/
    ingredients: {
      label: "Ingredients",
      size: 20,
      position: 14,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    /**************************************************************************
     * THIRD ROW                                                              *
     **************************************************************************/
    nonERXAdministrationFormat: {
      label: "Non-eRx Administration Format",
      size: 20,
      position: 13,
      defaultValue: "No Details Entered (via User)",
    },
    prescriptionDrugFormat: {
      label: "Drug Format",
      size: 20,
      position: 13,
      defaultValue: "No Details Provided (via ERX)",
    },
    dosageFrequency: {
      label: "Frequency",
      size: 20,
      position: 12,
      capitalizeValue: false,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    symptoms: {
      label: "Symptoms",
      size: 20,
      position: 11,
      defaultValue: "No Details Entered (via User)",
    },
    orderInstructions: {
      label: "Order Instructions",
      size: 20,
      position: 10,
      defaultValue: "No Details Entered (via User)",
    },
    PRN: {
      label: "PRN",
      size: 18,
      position: 9,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    /**************************************************************************
     * FOURTH ROW                                                             *
     **************************************************************************/
    PRNReasons: {
      label: "PRN Reasons",
      size: 20,
      position: 8,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    categories: {
      label: "Body Systems",
      size: 20,
      position: 7,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    orderingProviderDisplayName: {
      label: "Ordering Provider",
      size: 20,
      position: 6,
      defaultValue: "-",
    },
    providerNotes: {
      label: "Provider Notes",
      size: 20,
      position: 5,
      defaultValue: "No Details Entered (via User)",
    },
    hospicePays: {
      label: "Hospice Coverage",
      size: 20,
      position: 4,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    /**************************************************************************
     * FIFTH ROW                                                              *
     **************************************************************************/
    coverageDesignation: {
      label: "Reason for Non-Coverage",
      size: 20,
      position: 3,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    approvalType: {
      label: "Provider Order",
      size: 20,
      position: 2,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    readBackAndVerified: {
      label: "Read Back & Verified",
      size: 20,
      position: 1,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
  },
};

export const ALLOWED_PROPS_MEDICATION_NON_COMPOUND: AllowedProps = {
  MEDICATION: {
    /**************************************************************************
     * FIRST ROW                                                              *
     **************************************************************************/
    dosageStrength: {
      label: "Strength",
      size: 20,
      position: 18,
      capitalizeValue: false,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    dosageDosage: {
      label: "Dosage",
      size: 20,
      position: 17,
      capitalizeValue: false,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    dosageForm: {
      label: "Dosage Form",
      size: 20,
      position: 16,
      capitalizeValue: false,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    dosageRoute: {
      label: "Route",
      size: 20,
      position: 15,
      capitalizeValue: false,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    createdDatetime: {
      label: "Order Enter Date",
      size: 20,
      position: 14,
      capitalizeValue: false,
      defaultValue: "-",
    },
    /**************************************************************************
     * SECOND ROW                                                             *
     **************************************************************************/
    nonERXAdministrationFormat: {
      label: "Non-eRx Administration Format",
      size: 20,
      position: 13,
      defaultValue: "No Details Entered (via User)",
    },
    prescriptionDrugFormat: {
      label: "Drug Format",
      size: 20,
      position: 13,
      defaultValue: "No Details Provided (via ERX)",
    },
    dosageFrequency: {
      label: "Frequency",
      size: 20,
      position: 12,
      capitalizeValue: false,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    symptoms: {
      label: "Symptoms",
      size: 20,
      position: 11,
      defaultValue: "No Details Entered (via User)",
    },
    orderInstructions: {
      label: "Order Instructions",
      size: 20,
      position: 10,
      defaultValue: "No Details Entered (via User)",
    },
    PRN: { label: "PRN", size: 20, position: 9, defaultValue: "-" },
    /**************************************************************************
     * THIRD ROW                                                              *
     **************************************************************************/
    PRNReasons: {
      label: "PRN Reasons",
      size: 20,
      position: 8,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    categories: {
      label: "Body Systems",
      size: 20,
      position: 7,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    orderingProviderDisplayName: {
      label: "Ordering Provider",
      size: 20,
      position: 6,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    providerNotes: {
      label: "Provider Notes",
      size: 20,
      position: 5,
      defaultValue: "No Details Entered (via User)",
    },
    hospicePays: {
      label: "Hospice Coverage",
      size: 20,
      position: 4,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    /**************************************************************************
     * FOURTH ROW                                                             *
     **************************************************************************/
    coverageDesignation: {
      label: "Reason for Non-Coverage",
      size: 20,
      position: 3,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    approvalType: {
      label: "Provider Order",
      size: 20,
      position: 2,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
    readBackAndVerified: {
      label: "Read Back & Verified",
      size: 20,
      position: 1,
      defaultValue: (order, _property) => {
        return order.orderType === "MEDICATION" && order.orderSubtype === "ERX"
          ? "No Details Provided (via ERX)"
          : "No Details Entered (via User)";
      },
    },
  },
};

export const ALLOWED_PROPS_OE: AllowedProps = {
  DME: {
    // REQUIRED
    categories: {
      label: "Body Systems",
      size: 16,
      position: 7,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    issue: {
      label: "Issue",
      size: 18,
      position: 6,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    nameDescription: {
      label: "DME Type",
      size: 25,
      position: 5,
      defaultValue: "Required Field Data Is Missing",
    },
    // REQUIRED
    hospicePays: {
      label: "Hospice Coverage",
      size: 16,
      position: 4,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    coverageDesignation: {
      label: "Reason for Non-Coverage",
      size: 18,
      position: 3,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    approvalType: {
      label: "Provider Order",
      size: 18,
      position: 2,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    providerNotes: {
      label: "Provider Notes",
      size: 25,
      position: 1,
      defaultValue: "No Details Entered (via User)",
    },
  },
  VISIT_FREQUENCY: {
    // OPTIONAL
    visits: {
      label: "Visits",
      size: 22,
      position: 8,
      min: true,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    timeInterval: {
      label: "Frequency",
      size: 22,
      position: 7,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    duration: {
      label: "Duration",
      size: 22,
      position: 6,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    discontinueDate: {
      label: "Discontinue Date",
      size: 22,
      position: 5,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    PRNQuantity: {
      label: "PRN Quantity",
      size: 22,
      position: 4,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    PRNReasons: {
      label: "PRN Reason",
      size: 22,
      position: 3,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    approvalType: {
      label: "Provider Order",
      size: 22,
      position: 2,
      min: true,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    providerNotes: {
      label: "Provider Notes",
      size: 22,
      position: 1,
      defaultValue: "No Details Entered (via User)",
    },
  },
  DIAGNOSIS: {
    // REQUIRED
    diagnosisType: {
      label: "Diagnosis Type",
      size: 19,
      position: 3,
      defaultValue: "Required Field Data Is Missing",
    },
    // REQUIRED
    approvalType: {
      label: "Provider Order",
      size: 19,
      position: 2,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    providerNotes: {
      label: "Provider Notes",
      size: 50,
      position: 1,
      defaultValue: "No Details Entered (via User)",
    },
  },
  OTHER: {
    // REQUIRED
    hospicePays: {
      label: "Hospice Coverage",
      size: 18,
      position: 6,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    coverageDesignation: {
      label: "Reason for Non-Coverage",
      size: 18,
      position: 5,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    approvalType: {
      label: "Provider Order",
      size: 12,
      position: 4,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    readBackAndVerified: {
      label: "Read Back & Verified",
      size: 12,
      position: 3,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    providerNotes: {
      label: "Provider Notes",
      size: 35,
      position: 2,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    orderInstructions: {
      label: "Order Instructions",
      size: 35,
      position: 1,
      defaultValue: "No Details Entered (via User)",
    },
  },
  LEVEL_OF_CARE: {
    // REQUIRED
    careLocationType: {
      label: "Care Location Type",
      size: 20,
      position: 7,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    careLocation: {
      label: "Care Location",
      size: 20,
      position: 6,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    patientLocation: {
      label: "Patient Location",
      size: 20,
      position: 5,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    patientLocationPhoneNumber: {
      label: "Phone Number",
      size: 20,
      position: 4,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    patientLocationFaxNumber: {
      label: "Fax Number",
      size: 20,
      position: 3,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    approvalType: {
      label: "Provider Order",
      size: 20,
      position: 2,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    providerNotes: {
      label: "Provider Notes",
      size: 20,
      position: 1,
      defaultValue: "No Details Entered (via User)",
    },
  },
};

export const ALLOWED_PROPS_OM: AllowedProps = {
  DME: {
    // REQUIRED
    categories: {
      label: "Body Systems",
      size: 16,
      position: 7,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    issue: {
      label: "Issue",
      size: 18,
      position: 6,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    nameDescription: {
      label: "DME Type",
      size: 25,
      position: 5,
      defaultValue: "Required Field Data Is Missing",
    },
    // REQUIRED
    hospicePays: {
      label: "Hospice Coverage",
      size: 16,
      position: 4,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    coverageDesignation: {
      label: "Reason for Non-Coverage",
      size: 18,
      position: 3,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    approvalType: {
      label: "Provider Order",
      size: 18,
      position: 2,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    providerNotes: {
      label: "Provider Notes",
      size: 25,
      position: 1,
      defaultValue: "Something Has Gone Wrong ...",
    },
  },
  VISIT_FREQUENCY: {
    // OPTIONAL
    visits: {
      label: "Visits",
      size: 22,
      position: 8,
      min: true,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    timeInterval: {
      label: "Frequency",
      size: 22,
      position: 7,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    duration: {
      label: "Duration",
      size: 22,
      position: 6,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    discontinueDate: {
      label: "Discontinue Date",
      size: 22,
      position: 5,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    PRNQuantity: {
      label: "PRN Quantity",
      size: 22,
      position: 4,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    PRNReasons: {
      label: "PRN Reason",
      size: 22,
      position: 3,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    approvalType: {
      label: "Provider Order",
      size: 22,
      position: 2,
      min: true,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    providerNotes: {
      label: "Provider Notes",
      size: 22,
      position: 1,
      defaultValue: "No Details Entered (via User)",
    },
  },
  DIAGNOSIS: {
    // REQUIRED
    diagnosisType: {
      label: "Diagnosis Type",
      size: 19,
      position: 3,
      defaultValue: "Required Field Data Is Missing",
    },
    // REQUIRED
    approvalType: {
      label: "Provider Order",
      size: 19,
      position: 2,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    providerNotes: {
      label: "Provider Notes",
      size: 50,
      position: 1,
      defaultValue: "No Details Entered (via User)",
    },
  },
  OTHER: {
    // REQUIRED
    hospicePays: {
      label: "Hospice Coverage",
      size: 18,
      position: 6,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    coverageDesignation: {
      label: "Reason for Non-Coverage",
      size: 18,
      position: 5,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    approvalType: {
      label: "Provider Order",
      size: 12,
      position: 4,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    readBackAndVerified: {
      label: "Read Back & Verified",
      size: 12,
      position: 3,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    providerNotes: {
      label: "Provider Notes",
      size: 35,
      position: 2,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    orderInstructions: {
      label: "Order Instructions",
      size: 35,
      position: 1,
      defaultValue: "No Details Entered (via User)",
    },
  },
  LEVEL_OF_CARE: {
    // REQUIRED
    careLocationType: {
      label: "Care Location Type",
      size: 20,
      position: 7,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    careLocation: {
      label: "Care Location",
      size: 20,
      position: 6,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    patientLocation: {
      label: "Patient Location",
      size: 20,
      position: 5,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    patientLocationPhoneNumber: {
      label: "Phone Number",
      size: 20,
      position: 4,
      defaultValue: "No Details Entered (via User)",
    },
    // OPTIONAL
    patientLocationFaxNumber: {
      label: "Fax Number",
      size: 20,
      position: 3,
      defaultValue: "No Details Entered (via User)",
    },
    // REQUIRED
    approvalType: {
      label: "Provider Order",
      size: 20,
      position: 2,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    providerNotes: {
      label: "Provider Notes",
      size: 20,
      position: 1,
      defaultValue: "No Details Entered (via User)",
    },
  },
};

export const ALLOWED_PROPS_VISIT_FREQUENCY_DECLINED: AllowedProps = {
  VISIT_FREQUENCY: {
    // REQUIRED
    declinedDate: {
      label: "Date Declined",
      size: 22,
      position: 10,
      defaultValue: "Required Field Data Is Missing",
    },
    // OPTIONAL
    declinedReason: {
      label: "Declined Reason",
      size: 22,
      position: 9,
      defaultValue: "No Details Entered (via User)",
    },
  },
};



// WEBPACK FOOTER //
// ./src/configs/om-details-config.ts